<template>
    <div class="ah-page external-invoice-page">
        <div class="page-header">
            <div class="ah-container">
                <div class="title-row">
                    <div class="title">
                        <h1>外部進項發票</h1>
                        <div class="title-sub-text">因特殊情形下有公司統編的進項發票、但非由公司實際支出，在此記錄統計。</div>
                    </div>
                    <div class="actions">
                        <el-button v-if="!isQR()" type="highlight" @click="onAdd">新增進項發票</el-button>
                    </div>
                </div>
            </div>
        </div>
        <div class="ah-container">
            <div class="ah-panel" style="margin-top: 25px">
                <template v-if="!draftMode">
                    <div class="panel-header" style="border: 0">
                        <div class="panel-title">
                            <el-select v-model="year" placeholder="選擇年份" @change="onTimeChange">
                                <el-option v-for="y in yearList" :label="y.label" :value="y.value" :key="y.value"></el-option>
                            </el-select>
                            <el-select v-model="phase" placeholder="期別" style="margin-left: 10px" @change="onTimeChange">
                                <el-option v-for="p in phaseOptions" :label="p.label" :value="p.sno" :key="p.sno"></el-option>
                            </el-select>
                        </div>
                        <div class="panel-action">
                            <el-button @click="onToggleDraft"> 草稿 </el-button>
                            <el-button @click="onExport"> 匯出Excel </el-button>
                        </div>
                    </div>

                    <div class="panel-header summary-block">
                        <div class="panel-title">
                            <div class="summary-title">本期外部進項發票未稅金額總計</div>
                            <div class="summary-num">
                                <Money :v="totalAmt - totalTax" />
                            </div>
                        </div>
                        <div class="panel-title">
                            <div class="summary-title">本期外部進項發票稅額總計</div>
                            <div class="summary-num">
                                <Money :v="totalTax" />
                            </div>
                        </div>
                        <div class="panel-title">
                            <div class="summary-title">本期外部進項發票金額總計</div>
                            <div class="summary-num">
                                <Money :v="totalAmt" />
                            </div>
                        </div>
                    </div>
                </template>
                <template v-else>
                    <div class="panel-header" style="border: 0; padding-bottom: 0">
                        <div class="panel-title">
                            <el-button @click="onToggleDraft"> 回進項發票 </el-button>
                        </div>
                    </div>
                    <div class="panel-header">
                        <div class="panel-title">
                            <el-alert :closable="false" type="warning">
                                請補齊下列進項發票草稿的：發票類型、發票號碼、發票日期、金額。
                            </el-alert>
                        </div>
                    </div>
                </template>

                <div class="panel-body">
                    <TableView
                        :datas="datas"
                        :loading="isLoading"
                        :year="year"
                        :phase="phase"
                        :pageSize="queryParams.PageSize"
                        :currPage="queryParams.PageNum"
                        @refresh="updateData"
                    />
                    <el-pagination
                        layout="prev, pager, next"
                        :page-size.sync="queryParams.PageSize"
                        :total.sync="totalDataCount"
                        :current-page.sync="queryParams.PageNum"
                        :small="isMobile()"
                        class="pagination"
                        @current-change="onPageChange"
                    >
                    </el-pagination>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import ExportMixins from './ExportMixins';
import TableView from './TableView.vue';

export default {
    mixins: [ExportMixins],
    components: { TableView },
    data() {
        return {
            datas: [],
            isLoading: false,
            draftMode: false,

            queryParams: {
                PageSize: 25,
                PageNum: 1,
                StartDate: null,
                EndDate: null,
            },
            year: moment().year(),
            yearList: [],
            phase: 1,
            phaseOptions: [
                {
                    sno: 1,
                    label: '1 ~ 2月',
                },
                {
                    sno: 2,
                    label: '3 ~ 4月',
                },
                {
                    sno: 3,
                    label: '5 ~ 6月',
                },
                {
                    sno: 4,
                    label: '7 ~ 8月',
                },
                {
                    sno: 5,
                    label: '9 ~ 10月',
                },
                {
                    sno: 6,
                    label: '11 ~ 12月',
                },
            ],

            totalDataCount: 0,
            totalAmt: 0,
            totalTax: 0,
        };
    },
    created() {
        this.initYearSelect();
        this.init();
        this.updateData();
    },
    methods: {
        initYearSelect() {
            const START = 2020;
            const END = moment().year();

            for (let i = END; i >= START; i -= 1) {
                this.yearList.push({ label: i, value: i });
            }
        },
        init() {
            const { draft } = this.$route.query;
            const year = Number(this.$route.query.year);
            const phase = Number(this.$route.query.phase);

            if (draft) this.draftMode = draft;

            if (year && phase) {
                this.year = year;
                this.phase = phase;
            } else {
                const THIS_MONTH = moment().month() + 1;
                this.phase = Math.ceil(THIS_MONTH / 2);
            }
        },
        onPageChange() {
            this.updateData();
        },
        onTimeChange() {
            this.updateData();
        },
        async updateData() {
            this.isLoading = true;
            this.setDateQuery();

            try {
                const resp = await this.$api({
                    url: 'PaymentInvoice/QueryPaymentInvoice',
                    method: 'GET',
                    params: this.draftMode
                        ? {
                            PageNum: this.queryParams.PageNum,
                            PageSize: this.queryParams.PageSize,
                            draft: true,
                        }
                        : this.queryParams,
                });

                console.log('External Invoices', resp.data);
                this.datas = resp.data.items;
                this.totalDataCount = resp.data.totalNum;
                this.totalAmt = resp.data.totalAmt;
                this.totalTax = resp.data.totalTax;
            } finally {
                this.isLoading = false;
            }
        },
        setDateQuery() {
            const sd = moment()
                .year(this.year)
                .month(this.phase * 2 - 2)
                .startOf('month');

            const ed = moment()
                .year(this.year)
                .month(this.phase * 2 - 1)
                .endOf('month');

            this.queryParams.StartDate = sd.format('x');
            this.queryParams.EndDate = +ed.format('x') - 999;

            if (typeof this.draftMode === 'boolean') {
                this.$router.replace({
                    query: {
                        year: this.year,
                        phase: this.phase,
                        draft: this.draftMode ? 1 : undefined,
                    },
                });
            }
        },
        onAdd() {
            this.$router.push({ name: 'tax-credit-invoice-add' });
        },
        onToggleDraft() {
            this.draftMode = !this.draftMode;
            this.updateData();
        },
    },
};
</script>

<style lang="scss" scoped>
.external-invoice-page {
    .page-header > .ah-container > .title-row > .title {
        line-height: 100%;
        padding: 32px 20px 32px 20px;

        h1 {
            margin-bottom: 15px;
        }
        .title-sub-text {
            font-size: 10pt;
        }
    }
    .summary-block {
        display: flex;
    }
    .summary-title {
        font-size: 10pt;
    }
    @media (max-width: 720px) {
        .summary-block {
            flex-wrap: wrap;
        }
    }
}
</style>
